import { ClockCircleOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import type { ConsentEvent } from "../types/consent.types.ts"

/**
 * One consent change: the state it left and the state it set, who recorded it
 * and when EZBuild stored it, the day the person asked, the source and the reason.
 */
export const ConsentEventEntry = ({ event }: { event: ConsentEvent }) => {
  const { t } = useTranslation()
  // `stated_on` is a calendar day: formatted as written, never moved into a zone.
  const { formatDate, dateFormat } = useDateFormatter()
  const stateLabel = (value: string) =>
    event.field === "marketing_opt_in"
      ? t(
          value === "true"
            ? "communication.consent.marketingYes"
            : "communication.consent.marketingNo"
        )
      : t(`recordComm.state.${event.channel}.${value}`, {
          defaultValue: t(`communication.consent.state.${value}`, {
            defaultValue: value
          })
        })
  const details: Array<[string, string]> = [
    [
      t("recordComm.prefs.recordedBy"),
      event.recorded_by_name ?? t("recordComm.prefs.system")
    ],
    [
      t("recordComm.prefs.recordedAt"),
      String(formatDate(event.occurred_at, "MMM D, YYYY h:mm A"))
    ],
    ...(event.stated_on
      ? ([
          [
            t("recordComm.prefs.requestedOnLabel"),
            dayjs(event.stated_on).format(dateFormat)
          ]
        ] as Array<[string, string]>)
      : []),
    [
      t("recordComm.prefs.source"),
      t(`communication.consent.source.${event.source}`, {
        defaultValue: event.source
      })
    ],
    ...(event.reason
      ? ([[t("recordComm.prefs.reason"), event.reason]] as Array<
          [string, string]
        >)
      : [])
  ]
  return (
    <li className="flex gap-3 text-sm">
      <ClockCircleOutlined aria-hidden className="mt-1 text-slate-400" />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="font-medium text-slate-800">
          {event.from_value
            ? t("recordComm.prefs.eventChange", {
                channel: t(`commHub.channel.${event.channel}`),
                from: stateLabel(event.from_value),
                to: stateLabel(event.to_value)
              })
            : t("recordComm.prefs.event", {
                channel: t(`commHub.channel.${event.channel}`),
                to: stateLabel(event.to_value)
              })}
        </span>
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
          {details.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className="text-slate-500">{label}</dt>
              <dd className="m-0 break-words text-slate-700">{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    </li>
  )
}
